import React, { useRef, useState } from "react";
import styles from "./UploadFile.module.scss";
import classNames from "classnames/bind";
import { DowloadFIleIcon } from "~/assets/icons";
import BaseInputV2 from "~/components/common/base-input-v2/BaseInputV2";
const cx = classNames.bind(styles);

interface UploadFileModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (data: any) => void;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({
  isOpen,
  onCancel,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    fileSize: "",
    fileName: "",
    description: "",
    file: null as File | null,
    fileType: "",
  });
  const [error, setError] = useState({
    description: "",
    file: "",
  });
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  

  const formatFileSize = (size: number) => {
    if (size >= 1024 ** 3) return `${(size / 1024 ** 3).toFixed(2)} GB`;
    if (size >= 1024 ** 2) return `${(size / 1024 ** 2).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${size} B`;
  };

  const allowedTypes = {
    "application/pdf": "pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = allowedTypes[file.type as keyof typeof allowedTypes];
      if (!ext) {
        setError({ ...error, file: "Only PDF, XLSX, DOCX files are allowed." });
        return;
      }
      setFormData({
        ...formData,
        file,
        fileName: `${file.name}`,
        fileSize: formatFileSize(file.size),
        fileType: ext,
      });
      setError({ ...error, file: "" });
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const ext = allowedTypes[file.type as keyof typeof allowedTypes];
      if (!ext) {
        setError({ ...error, file: "Only PDF, XLSX, DOCX files are allowed." });
        return;
      }
      setFormData({
        ...formData,
        file,
        fileName: `${file.name}`,
        fileSize: formatFileSize(file.size),
        fileType: ext,
      });
      setError({ ...error, file: "" });
    }
  };

  const handleUpload = () => {
    let hasError = false;
    let newError = { description: "", file: "" };

    if (!formData.file) {
      newError.file = "Please select a file.";
      hasError = true;
    }
    if (!formData.description.trim()) {
      newError.description = "Please enter a description.";
      hasError = true;
    }

    setError(newError);

    if (!hasError) {
      onSave(formData);
    }
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  return (
    <div className={cx("overlay")} onClick={onCancel}>
      <div
        className={cx("upload-file-modal-container")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cx("header")}>
          <h4 className={cx("header-title")}>Upload File</h4>
          <p className={cx("header-description")}>Create new file</p>
        </div>

        <div className={cx("content")}>
          <div>
            <div
              className={cx("", {
                "error-upload": error.file !== "",
                "drag-active": isDragActive,
              })}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {formData.file ? (
                // <div className={cx("file-info")}>
                  // <p>
                  //   <strong>Selected file:</strong> {formData.fileName} (
                  //   {formData.fileSize})
                  // </p>
                // </div>
                <div className={cx('preview')}>
                  <p className={cx('file-info')}>
                    {formData.fileName} (
                    {formData.fileSize})
                  </p>
                  <div className={cx('divider')}></div>
                  <div className={cx('btn-remove')} onClick={openFileDialog}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.75 18.75H16.75C17.8546 18.75 18.75 17.8546 18.75 16.75V2.75C18.75 1.64543 17.8546 0.75 16.75 0.75L2.75 0.75C1.64543 0.75 0.75 1.64543 0.75 2.75L0.75 16.75C0.75 17.8546 1.64543 18.75 2.75 18.75ZM2.75 18.75L13.75 7.75L18.75 12.75M7.75 6.25C7.75 7.07843 7.07843 7.75 6.25 7.75C5.42157 7.75 4.75 7.07843 4.75 6.25C4.75 5.42157 5.42157 4.75 6.25 4.75C7.07843 4.75 7.75 5.42157 7.75 6.25Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <p>Change</p>
                  </div>
                </div>
              ) : (
                <div className={cx("upload-section", {
                  "error-upload": error.file !== "",
                  "drag-active": isDragActive,
                })}>
                  <p className={cx("upload-text")}>
                    Drag and drop your file here or <span className={cx('click-upload')}  onClick={openFileDialog}>Click to upload</span>
                  </p>
                  <p className={cx("upload-description")}>Maximum file size: 10MB</p>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.docx,.xlsx"
                style={{ display: "none" }}
                id="file-upload"
                onChange={handleFileChange}
              />
            </div>
            {error.file && <p className={cx("error-message")}>{error.file}</p>}
          </div>

          <BaseInputV2
            type="text"
            label="Description"
            placeholder="Enter description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            errorMessage={error.description}
          />
        </div>

        <div className={cx("footer")}>
          <button className={cx("footer-cancel-button")} onClick={onCancel}>
            Cancel
          </button>
          <button
            className={cx("footer-save-button")}
            onClick={handleUpload}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadFileModal;
